import { getLocale, t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { PopoverSurface } from "@ui/components/PopoverSurface";
import { SoftButton } from "@ui/components/SoftButton";
import { formatPriceWhole } from "@ui/lib/format-price";
import { DialogTrigger } from "@ui/primitives/Popover";
import { factorLabel } from "@/lib/ranking-labels";

export interface RankingTier {
  factors: string[];
  /** The tier's percentage of the total ranking weight, as the manifest sets it. */
  share: number;
  tier: string;
}

export interface RankingExplainerProps {
  /** Monthly budget in cents the ranking hinged affordability on, or null when
   *  the family named none and it anchored on the list's own middle. */
  budget: number | null;
  /** Tier membership as the manifest declares it, heaviest tier first. */
  tiers: RankingTier[];
  /** Whether the reader named those months, or the page fell back to the one
   *  we are in. The months are the same shape either way; only the sentence
   *  that introduces them differs. */
  datesNamed: boolean;
  /** The months the seasonal factors were read for. Never empty — an unnamed
   *  window resolves to the current month rather than to the whole year. */
  windowMonths: number[];
}

// Intl carries the separator and the conjunction; a hard-coded ", " is wrong in
// several of the six locales we ship.
function formatList(items: string[]): string {
  return new Intl.ListFormat(getLocale(), { style: "long", type: "conjunction" }).format(items);
}

// 2026 is arbitrary and never shown — only the month name is formatted.
function monthName(month: number): string {
  return new Intl.DateTimeFormat(getLocale(), { month: "long" }).format(new Date(2026, month - 1, 1));
}

function tierLabel(tier: string): string {
  const labels: Record<string, string> = {
    decisive: t("components.views.destinations.ranking.tiers.decisive"),
    shaping: t("components.views.destinations.ranking.tiers.shaping"),
    tiebreak: t("components.views.destinations.ranking.tiers.tiebreak"),
    working: t("components.views.destinations.ranking.tiers.working"),
  };
  return labels[tier] ?? tier;
}

// Inertia restores serialized page props from history.state, so a tab loaded
// before this prop shipped comes back without it on Back.
export function RankingExplainer({ budget, datesNamed, tiers = [], windowMonths }: RankingExplainerProps) {
  const total = tiers.reduce((count, tier) => count + tier.factors.length, 0);
  if (total === 0) return null;

  return (
    <DialogTrigger>
      <SoftButton
        aria-label={t("components.views.destinations.ranking.trigger_label")}
        className="size-5 text-neutral-500 outline-none transition-colors hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
      >
        <Icon name="info" size="12" />
      </SoftButton>
      <PopoverSurface className="max-w-[26rem] p-5" placement="bottom start">
        <div className="flex flex-col gap-4">
          <h2 className="font-semibold text-base text-neutral-900">
            {t("components.views.destinations.ranking.title")}
          </h2>
          <p className="text-neutral-600 text-sm">
            {t("components.views.destinations.ranking.intro", { count: total })}
          </p>

          {tiers.map((tier) => (
            <div className="flex flex-col gap-1" key={tier.tier}>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-medium text-neutral-500 text-xs uppercase tracking-wider">
                  {tierLabel(tier.tier)}
                </h3>
                <span className="text-neutral-400 text-xs tabular-nums">
                  {t("components.views.destinations.ranking.tier_share", { share: tier.share })}
                </span>
              </div>
              <p className="text-neutral-700 text-sm">{formatList(tier.factors.map(factorLabel))}</p>
            </div>
          ))}

          {/* What this particular ranking was for. Publishing one global set of
              shares while the budget and window move the order would describe a
              ranking the reader is not looking at. */}
          <div className="flex flex-col gap-2 border-neutral-100 border-t pt-4">
            <p className="text-neutral-600 text-sm">
              {datesNamed
                ? t("components.views.destinations.ranking.window_note_months", {
                    months: formatList(windowMonths.map(monthName)),
                  })
                : t("components.views.destinations.ranking.window_note_current", {
                    months: formatList(windowMonths.map(monthName)),
                  })}
            </p>
            <p className="text-neutral-600 text-sm">
              {budget === null
                ? t("components.views.destinations.ranking.budget_note_median")
                : t("components.views.destinations.ranking.budget_note_set", {
                    amount: formatPriceWhole(budget, "USD"),
                  })}
            </p>
          </div>

          <div className="flex flex-col gap-2 border-neutral-100 border-t pt-4">
            <p className="text-neutral-600 text-sm">{t("components.views.destinations.ranking.balance")}</p>
            <p className="text-neutral-600 text-sm">{t("components.views.destinations.ranking.missing")}</p>
            <p className="text-neutral-600 text-sm">{t("components.views.destinations.ranking.coverage")}</p>
            <p className="text-neutral-600 text-sm">{t("components.views.destinations.ranking.no_placement")}</p>
          </div>
        </div>
      </PopoverSurface>
    </DialogTrigger>
  );
}
